import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../../_services/api.service';
import { AuthService } from '../../../_services/auth.service';
import { Group } from '../../../_interfaces/group';
import { DocumentType } from '../../../_interfaces/document-type';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';
import { SelectSkeletonComponent } from '../../../shared/components/select-skeleton/select-skeleton.component';
import { SkeletonComponent } from '../../../shared/components/skeleton/skeleton.component';
import { TranslateService } from '@ngx-translate/core';
import { APP_DIALOG_DATA } from '../../components/dialog/dialog.tokens';
import { AppDialogRef } from '../../components/dialog/dialog-ref';
import { DIALOG_IMPORTS } from '../../components/dialog/dialog.imports';
import { CopilotRecipient, CopilotRecipients } from '../../../_interfaces/document';
import {
  ccRecipients,
  hasPrimaryRecipient,
  sortRecipientsByName,
} from '../../../document/components/recipients/recipient.helpers';
import { RecipientDetailsComponent } from '../../../document/components/recipients/recipient-details.component';
import { openCopilotSnackbar } from '../../components/copilot-snackbar/open-copilot-snackbar';
import { TemplateType } from '../../../_interfaces/template';
import { AnalyticsService } from '../../../_services/analytics.service';

export interface ApproveWorkflowDialogData {
  title?: string;
  description?: string;
  dictationId?: string;
  dictationDuration?: number;
  externalTemplateId?: number;
  externalTemplateType?: TemplateType;
  priority?: 1 | 2 | 3;
  subjectName?: string;
  subjectFirstName?: string;
  subjectMiddleName?: string;
  subjectLastName?: string;
  subjectAlternativeId?: string;
  subjectId?: string;
  subjectDob?: string;
  visitId?: string;
  recipients?: CopilotRecipients;
  recipientManagementEnabled?: boolean;
}

@Component({
  selector: 'app-approve-workflow-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ...DIALOG_IMPORTS,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    TranslateModule,
    LoadingSpinnerComponent,
    SelectSkeletonComponent,
    SkeletonComponent,
    RecipientDetailsComponent,
  ],
  templateUrl: './approve-workflow.component.html',
  host: {
    class: 'flex min-h-0 max-h-[calc(100dvh-3rem)] flex-col overflow-y-auto',
  },
})

export class ApproveWorkflowDialogComponent implements OnInit {
  authorGroups: Group[] = [];
  selectedGroupId: number | null = null;
  canSendToEditor = false;
  documentTypes: DocumentType[] = [];
  selectedTemplateId: number | null = null;
  isSubmitting = false;
  submittingAction: 'complete' | 'next' | null = null;
  groupWithRoute = false;
  routeCheckFailed = false;
  alertMessage: string | null = null;
  isLoadingGroups = true;
  isLoadingTemplates = false;
  private groupTemplatesCache = new Map<number, DocumentType[]>();

  constructor(
    private dialogRef: AppDialogRef<ApproveWorkflowDialogComponent>,
    @Inject(APP_DIALOG_DATA) public data: ApproveWorkflowDialogData,
    private apiService: ApiService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private readonly analytics: AnalyticsService,
  ) {}

  get isLoading(): boolean {
    return this.isLoadingGroups || this.isLoadingTemplates;
  }

  private get hasSelection(): boolean {
    return (
      !this.isLoading &&
      !this.routeCheckFailed &&
      this.selectedGroupId != null &&
      this.selectedTemplateId != null &&
      this.authorGroups.length > 0
    );
  }

  get canApprove(): boolean {
    if (this.isSubmitting && this.submittingAction === 'complete') return false;
    return this.hasSelection;
  }

  get canSubmitToEditor(): boolean {
    if (this.isSubmitting && this.submittingAction === 'next') return false;
    return this.hasSelection && this.canSendToEditor;
  }

  get hideGroupAndTemplate(): boolean {
    return !!this.data.recipientManagementEnabled;
  }

  get primaryRecipient(): CopilotRecipient | null {
    return this.data.recipients?.primaryRecipient ?? null;
  }

  get secondaryRecipients(): CopilotRecipient[] {
    return sortRecipientsByName(ccRecipients(this.data.recipients));
  }

  get showRecipientList(): boolean {
    return this.hideGroupAndTemplate && (hasPrimaryRecipient(this.data.recipients) || this.secondaryRecipients.length > 0);
  }

  async ngOnInit(): Promise<void> {
    const user = this.authService.getUser();
    if (!user) return;
    try {
      const groups = await this.apiService.getGroups('DictationGroup', user.id);
      this.authorGroups = groups;

      // Select default group if exists, otherwise first available
      const defaultGroupId = user.settings?.default_group_id;
      const hasDefault = typeof defaultGroupId === 'number' && this.authorGroups.some(g => g.id === defaultGroupId);
      const fallbackGroupId = hasDefault ? (defaultGroupId as number) : (this.authorGroups[0]?.id ?? null);

      // Prefer the group that actually contains the template being approved,
      // so it stays preselected even when it lives outside the default group.
      this.selectedGroupId = await this.findGroupForExternalTemplate(fallbackGroupId);

      if (this.selectedGroupId != null) {
        await this.afterGroupSelected(this.selectedGroupId);
      }
    } catch (error) {
      console.error('Error fetching author groups');
      console.error(error);
    } finally {
      this.isLoadingGroups = false;
    }
  }

  async onGroupChange(groupId: number): Promise<void> {
    this.selectedGroupId = groupId;
    await this.afterGroupSelected(groupId);
  }

  async submit(action: 'complete' | 'next'): Promise<void> {
    if (this.isSubmitting) return;
    if (this.routeCheckFailed) return;
    if (!this.selectedGroupId || !this.selectedTemplateId) return;
    this.isSubmitting = true;
    this.submittingAction = action;

    if (!this.groupWithRoute && action === 'complete') {
      this.dialogRef.close(-1);
      return;
    }

    const payload: any = {
      priority: this.data.priority ?? 1,
      authorGroupId: this.selectedGroupId,
      templateId: this.selectedTemplateId,
      properties: {
        audioFormat: 'wav',
        device: 'Copilot',
        createdAsAuthor: true
      },
      audioMeta: {
        size: 0,
        duration: this.data.dictationDuration ?? 0
      },
      action,
      dictationId: this.data.dictationId,
      externalTemplateId: this.data.externalTemplateId,
      externalTemplateType: this.data.externalTemplateType
    };

    if (this.data.subjectAlternativeId) {
      payload.subjectAlternativeId = this.data.subjectAlternativeId;
    }
    if (this.data.subjectId) {
      payload.subjectId = this.data.subjectId;
    }
    // hoffweb-api only stores subject_first/middle/last_name when subjectName is absent.
    const hasNameParts = Boolean(this.data.subjectFirstName || this.data.subjectMiddleName || this.data.subjectLastName);
    if (hasNameParts) {
      if (this.data.subjectFirstName) payload.subjectFirstName = this.data.subjectFirstName;
      if (this.data.subjectMiddleName) payload.subjectMiddleName = this.data.subjectMiddleName;
      if (this.data.subjectLastName) payload.subjectLastName = this.data.subjectLastName;
    } else if (this.data.subjectName) {
      payload.subjectName = this.data.subjectName;
    }
    if (this.data.subjectDob) {
      payload.dateBirth = this.data.subjectDob;
    }
    if (this.data.visitId) {
      payload.visitId = this.data.visitId;
    }

    try {
      const task = await this.apiService.createTask(payload);
      if (task && action === 'next') {
        this.analytics.track('document_sent_to_editor', {
          encounter_id: this.data.dictationId,
          blueprint_id: this.data.externalTemplateId != null
            ? String(this.data.externalTemplateId)
            : undefined,
          task_id: task.id,
        });
      }
      this.dialogRef.close(task.id);
    } catch (e) {
      // swallow for now; could add snackbar/error
      console.error(e);
    } finally {
      this.isSubmitting = false;
      this.submittingAction = null;
    }
  }

  private async afterGroupSelected(groupId: number): Promise<void> {
    this.isLoadingTemplates = true;
    await this.checkGroupRoute(groupId);
    if (this.selectedGroupId !== groupId) return;

    try {
      this.documentTypes = await this.loadGroupTemplates(groupId);
      const group = this.authorGroups.find(g => g.id === groupId) || null;
      const groupDefaultTemplateId = group?.default_template_id ?? null;
      const listIds = new Set(this.documentTypes.map(t => t.id));

      // Preselect: external template (from document being approved) -> group default -> first
      if (this.data.externalTemplateId && listIds.has(this.data.externalTemplateId)) {
        this.selectedTemplateId = this.data.externalTemplateId;
      } else if (groupDefaultTemplateId && listIds.has(groupDefaultTemplateId)) {
        this.selectedTemplateId = groupDefaultTemplateId;
      } else {
        this.selectedTemplateId = this.documentTypes[0]?.id ?? null;
      }
    } catch {
      this.documentTypes = [];
      this.selectedTemplateId = null;
    } finally {
      this.isLoadingTemplates = false;
    }
  }

  // Fetches every group's templates (when the dialog opens) to locate the group that
  // contains the template being approved. Prefers the fallback/default group when it
  // also has the template. Returns the fallback group when there is no match.
  private async findGroupForExternalTemplate(fallbackGroupId: number | null): Promise<number | null> {
    const externalTemplateId = this.data.externalTemplateId;
    if (!externalTemplateId || this.authorGroups.length === 0) {
      return fallbackGroupId;
    }

    const groupsWithTemplates = await Promise.all(
      this.authorGroups.map(async group => {
        try {
          return { groupId: group.id, templates: await this.loadGroupTemplates(group.id) };
        } catch {
          return { groupId: group.id, templates: [] as DocumentType[] };
        }
      })
    );

    const hasExternalTemplate = (templates: DocumentType[]) => templates.some(t => t.id === externalTemplateId);

    const fallbackMatch = groupsWithTemplates.find(g => g.groupId === fallbackGroupId && hasExternalTemplate(g.templates));
    if (fallbackMatch) {
      return fallbackMatch.groupId;
    }

    const match = groupsWithTemplates.find(g => hasExternalTemplate(g.templates));
    return match?.groupId ?? fallbackGroupId;
  }

  private async loadGroupTemplates(groupId: number): Promise<DocumentType[]> {
    const cached = this.groupTemplatesCache.get(groupId);
    if (cached) {
      return cached;
    }
    const templates = await this.apiService.getGroupTemplates(groupId);
    this.groupTemplatesCache.set(groupId, templates);
    return templates;
  }

  close(): void {
    this.dialogRef.close();
  }

  async checkGroupRoute(groupId: number): Promise<void> {
    this.routeCheckFailed = false;
    this.alertMessage = '';

    try {
      const canSendToEditor = await this.apiService.canSendToEditor(groupId);
      if (this.selectedGroupId !== groupId) return;
      this.canSendToEditor = canSendToEditor;
      this.groupWithRoute = true;
    } catch (error) {
      if (this.selectedGroupId !== groupId) return;
      this.canSendToEditor = false;
      this.groupWithRoute = false;

      if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
        this.routeCheckFailed = true;
        openCopilotSnackbar(
          this.snackBar,
          this.translate.instant('Unable to verify the transcription route. Please try again.')
        );
        return;
      }
    }

    if (!this.canSendToEditor && this.groupWithRoute) {
      this.alertMessage = this.translate.instant("There's no editing stage linked to this group's workflow");
    } else if (!this.groupWithRoute) {
      this.alertMessage = this.translate.instant(
        'There is no transcription route linked to the author group, approval will not create a task and will save the document in Scribe only'
      );
    } else {
      this.alertMessage = '';
    }
  }
}
